import { useEffect, useRef } from "react";
import { attachDerivedBranchTriggers } from "../commands/derivedBranchTriggers.js";
import { makeDefaultDeps } from "../commands/deps.js";
import { needsDerivedTriggers } from "../lib/forecast/derivedBranch.js";
import { runStore } from "../store/runStore.js";
import { reportFailure } from "../lib/failureCounts.js";

// Per tab, not per effect: StrictMode (and any remount) re-runs the effect, and a fresh set
// would ask again for a branch whose first ask may already have reached the paid generator.
const asked = new Set();

/** Test-only export; production callers must not use it. */
export function resetDerivedTriggerAsks() {
  asked.clear();
}

/**
 * A branch derived from a trigger's (w, a, b) lands instantly with no events, so each derived
 * binary branch that still has none needs an ask, including one reloaded mid-generation. The
 * server generates at most once per branch.
 *
 * An ask is never aborted by the effect's cleanup; its attach is a no-op once the run has left
 * the store.
 *
 * Takes no store refs, so this hook adds nothing to the `hooksTakingRunArgs` ratchet.
 *
 * @param {{ enabled: boolean, onAttached?: (runId: string, eventCount: number) => void }} args
 *   `enabled` is true for a signed-in owner with forecasts loaded; only the owner of a branch
 *   may ask.
 */
export function useDerivedBranchTriggers({ enabled, onAttached }) {
  const onAttachedRef = useRef(onAttached);
  useEffect(() => {
    onAttachedRef.current = onAttached;
  });
  useEffect(() => {
    if (!enabled) return undefined;
    const deps = makeDefaultDeps();
    let lastRuns = null;
    const scan = () => {
      const { runsById } = runStore.getState();
      if (runsById === lastRuns) return;
      lastRuns = runsById;
      for (const [runId, run] of Object.entries(runsById)) {
        if (asked.has(runId) || !needsDerivedTriggers(run)) continue;
        asked.add(runId);
        void attachDerivedBranchTriggers(deps, { runId })
          .then((outcome) => {
            if (outcome !== "attached") return;
            const events = runStore.getState().runsById[runId]?.events;
            onAttachedRef.current?.(runId, Array.isArray(events) ? events.length : 0);
          })
          .catch((err) => reportFailure("derived_branch_triggers.attach_failed", err));
      }
    };
    scan();
    return runStore.subscribe(scan);
  }, [enabled]);
}
